import { BaseComponent } from '../core/BaseComponent.js';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

/**
 * DatePicker - Renders a flatpickr calendar (MM/DD/YYYY display, YYYY-MM-DD storage).
 */
export class DatePicker extends BaseComponent {
    constructor(options = {}) {
        super(document, options);
        this.inputSelector = options.inputSelector || '.date-picker';
        this._instances    = [];
    }

    init() {
        this._initAll();
        this._setupCheckboxToggle();

        // Re-run after AJAX request-detail renders (follow_up_date loads via AJAX).
        document.addEventListener('docuswift:request-detail-rendered', () => {
            this._initAll();
        });

        return this;
    }

    /**
     * Attach flatpickr to every .date-picker that is not yet initialised.
     * Safe to call multiple times (e.g. after AJAX re-renders).
     */
    _initAll() {
        document.querySelectorAll(this.inputSelector).forEach(el => {
            if (el._flatpickr) return;

            flatpickr(el, {
                dateFormat:    'm/d/Y',
                allowInput:    true,
                disableMobile: true,
                appendTo:      document.body,
                parseDate(dateStr) {
                    if (!dateStr) return null;
                    const ymd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                    if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
                    const mdy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                    if (mdy) return new Date(+mdy[3], +mdy[1] - 1, +mdy[2]);
                    return new Date(dateStr);
                },
            });
        });
    }

    _setupCheckboxToggle() {
        ['change', 'input'].forEach(eventType => {
            document.addEventListener(eventType, (e) => {
                if (e.target &&
                    e.target.type === 'checkbox' &&
                    e.target.closest('.treatment-dates-wrapper')) {
                    this.toggleDateRange(e.target);
                }
            });
        });
    }

    /**
     * Format date input as MM/DD/YYYY (kept for legacy callers).
     */
    formatDate(input) {
        let value = input.value;
        if (/^[a-zA-Z]/.test(value)) return;
        value = value.replace(/\D/g, '');
        let formatted = '';
        if (value.length > 0) formatted += value.substring(0, 2);
        if (value.length > 2) formatted += '/' + value.substring(2, 4);
        if (value.length > 4) formatted += '/' + value.substring(4, 8);
        input.value = formatted;
    }

    /**
     * Attach flatpickr to a single input element.
     * Can be called externally (e.g. after AJAX renders new content).
     */
    static initInput(el) {
        if (!el || el._flatpickr) return;
        flatpickr(el, {
            dateFormat:    'm/d/Y',
            allowInput:    true,
            disableMobile: true,
            appendTo:      document.body,
            parseDate(dateStr) {
                if (!dateStr) return null;
                const ymd = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
                const mdy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
                if (mdy) return new Date(+mdy[3], +mdy[1] - 1, +mdy[2]);
                return new Date(dateStr);
            },
        });
    }

    /**
     * Convert any common date string to MM/DD/YYYY.
     */
    static toFullFormat(raw) {
        if (!raw) return '';
        raw = String(raw).trim();

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

        const mdyShort = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
        if (mdyShort) {
            const y = parseInt(mdyShort[3], 10);
            const fullYear = y <= 30 ? 2000 + y : 1900 + y;
            return mdyShort[1].padStart(2, '0') + '/' + mdyShort[2].padStart(2, '0') + '/' + fullYear;
        }

        const mdy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (mdy) {
            return mdy[1].padStart(2, '0') + '/' + mdy[2].padStart(2, '0') + '/' + mdy[3];
        }

        const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (ymd) {
            return ymd[2] + '/' + ymd[3] + '/' + ymd[1];
        }

        const ts = Date.parse(raw);
        if (!isNaN(ts)) {
            const d = new Date(ts);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${mm}/${dd}/${d.getFullYear()}`;
        }

        return raw;
    }

    /**
     * Toggle date range visibility based on checkbox.
     */
    toggleDateRange(checkbox) {
        const wrapper = checkbox.closest('.treatment-dates-wrapper');
        if (!wrapper) return;

        const dateRangeWrapper = wrapper.querySelector('.date-range-wrapper');
        if (!dateRangeWrapper) return;

        if (checkbox.checked) {
            dateRangeWrapper.style.display = 'flex';
            dateRangeWrapper.querySelectorAll('input').forEach(input => {
                input.setAttribute('required', true);
            });
        } else {
            dateRangeWrapper.style.display = 'none';
            dateRangeWrapper.querySelectorAll('input').forEach(input => {
                input.removeAttribute('required');
            });
        }
    }
}
